import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { Link as RouterLink } from 'react-router-dom'
import LockIcon from '@material-ui/icons/Lock'
import {
  Checkbox,
  Icon,
  Button,
  MenuList,
  Typography,
  Box,
} from '@material-ui/core'
import FetchTable from 'app/components/table/FetchTable'
import {
  gt,
  __,
  defaultTo,
  length,
  filter,
  compose,
  path,
  o,
  prop,
  isEmpty,
  isNil,
} from 'ramda'
import {
  withFirstColumn,
  withActionColumn,
} from 'app/components/table/TableColumn'
import EmptyStateView from 'app/components/EmptyStateView'
import TableMultiSelectMenu from 'app/components/TableMultiSelectMenu'
import { AccessType } from 'app/auth/accessControlService'
import { useAccessControl } from 'app/auth/useAccessControl'
import { isHomelessGroup } from 'commons/groupLogic'
import useStyles from '../styles'
import DeleteMenuItem from 'app/components/table/DeleteMenuItem'
import { Routes } from 'app/services/routeService/routeService'
import clsx from 'clsx'
import DialogError from 'app/main/group/components/dialogs/DialogError'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import AccessControl from '../../../auth/AccessControl'
import { useDispatch } from 'react-redux'
import { openDialog } from 'app/auth/store/actions/createNotification.actions'

import MessageAlert from 'app/components/MessageAlert'
import { ACTION_NEW, GENERAL_DIALOG } from 'app/constants/compose'

export default (props) => {
  const styleClass = useStyles()
  const dispatch = useDispatch()

  const {
      apiFetchersCallback: getSubscribersWithoutGroup,
      toggleDialogs,
      CREATE_GROUP_DIALOG,
      DELETE_DIALOG,
      pageData: {
        pageInfo,
        onFetchData,
        refetchData,
        toggleSelect,
        showPagination,
        toggleSelectAll,
        defaultPageSize,
      },
      onViewGroup,
      reRenderPage,
    } = props,
    { formatMessage } = useIntl()

  const [localState, setLocalState] = useState({
    subscribersWithoutGroup: null,
  })
  const [openErrorDialog, setOpenErrorDialog] = useState(false)

  const { checkedData, checkedIds, isCheckedAll, isIndeterminated } = pageInfo
  const { isGranted } = useAccessControl([AccessType.ADMINISTRATION])

  const checkGroupHaveCard = compose(
    gt(__, 0),
    length,
    filter(o(gt(__, 0), prop('membershipCardsCount'))),
    defaultTo([]),
  )

  useEffect(() => {
    getSubscribersWithoutGroup().then(({ data }) => {
      setLocalState({
        ...localState,
        subscribersWithoutGroup: data[0] || null,
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reRenderPage])

  const renderTableMenu = () => {
    return (
      <TableMultiSelectMenu>
        {(closeMenu) => (
          <MenuList>
            <DeleteMenuItem
              onClick={() => {
                closeMenu()
                checkGroupHaveCard(checkedData, pageInfo.data)
                  ? setOpenErrorDialog(true)
                  : toggleDialogs(DELETE_DIALOG, true, checkedData)
              }}
              selectedCount={length(checkedIds)}
            />
          </MenuList>
        )}
      </TableMultiSelectMenu>
    )
  }

  const hasOnlyHomelessData = () => {
    if (pageInfo.data.length === 1) {
      return compose(isHomelessGroup, path(['data', 0]))(pageInfo)
    }
    return false
  }

  const handlerDeleteOne = ({ original }) => {
    original.membershipCardsCount > 0
      ? setOpenErrorDialog(true)
      : toggleDialogs(DELETE_DIALOG, true, [original])
  }

  const handlerCreateOne = async ({ original }) => {
    const subGroups = original?.tagIds
      ?.split(',')
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNil(id))

    dispatch(
      openDialog({
        initialValues: {
          subGroups: subGroups,
        },
        onCreateSuccess: () => {
          dispatch(
            MessageAlert.showSuccess({
              message: formatMessage({
                id: 'createNotification.success.message',
              }),
            }),
          )
        },
        type: GENERAL_DIALOG,
        action: ACTION_NEW,
      }),
    )
  }

  const columns = [
    {
      ...withFirstColumn({
        Header: () => (
          <div>
            <Checkbox
              color="secondary"
              onClick={(event) => {
                event.stopPropagation()
              }}
              onChange={(event) => {
                toggleSelectAll(event.target.checked)
              }}
              checked={isCheckedAll}
              indeterminate={isIndeterminated}
            />
            {!isEmpty(checkedIds) &&
              !hasOnlyHomelessData() &&
              renderTableMenu()}
          </div>
        ),
        Cell: (cell) => {
          return [0].includes(cell.value['type']) ? (
            <Checkbox
              onClick={(event) => {
                event.stopPropagation()
              }}
              color="secondary"
              checked={checkedIds.includes(cell.value.id)}
              onChange={() => {
                toggleSelect(cell.value.id)
              }}
            />
          ) : (
            ''
          )
        },
        className: clsx('sticky fixBackground', styleClass.fixBackground),
        headerClassName: clsx('sticky', styleClass.fixBackground),
      }),
      show: isGranted,
    },
    {
      Header: () => (
        <div className="w-full text-left">
          <FormattedMessage id="group.table.th.groupName" />
        </div>
      ),
      Cell: ({ value, original }) => {
        return (
          <Typography className={(styleClass.groupName, 'font-400')}>
            {isHomelessGroup(original) && (
              <span className="flex items-center">
                <LockIcon className="ltr:mr-8 rtl:ml-8" /> {value}
              </span>
            )}
            {!isHomelessGroup(original) && value}
          </Typography>
        )
      },
      accessor: 'name',
    },
    {
      Header: () => (
        <div className="w-full text-center">
          <FormattedMessage id="group.table.th.subgroups" />
        </div>
      ),
      accessor: 'tagsCount',
      Cell: (props) => (
        <div className="w-full flex justify-center">
          <span className="number">{props.value}</span>
        </div>
      ),
      width: 150,
      sortable: false,
    },
    {
      Header: () => (
        <div className="w-full text-center">
          <FormattedMessage id="group.table.th.subscribers" />
        </div>
      ),
      accessor: 'subscribersCount',
      Cell: (props) => (
        <div className="w-full flex justify-center">
          <span className="number">{props.value}</span>
        </div>
      ),
      width: 150,
      sortable: false,
    },
    withActionColumn(
      handlerDeleteOne,
      handlerCreateOne,
      isGranted,
      (cell) =>
        cell.original &&
        cell.original['type'] &&
        ![0].includes(cell.original['type']),
    ),
  ]

  const shouldShowEmptyView = () => {
    return pageInfo.isFirsttimeFetchingNoData
  }

  let pageSize = defaultPageSize
  if (pageInfo.pageSize && pageInfo.pageSize !== pageSize) {
    pageSize = pageInfo.pageSize
  }

  return (
    <>
      <AccessControl
        accesses={[AccessType.ADMINISTRATION, AccessType.SUBSCRIBER_VIEW]}
      >
        <Box className={styleClass.tablePanel}>
          {localState.subscribersWithoutGroup && (
            <RouterLink
              className={styleClass.linkToWithoutSubscribers}
              to={{
                pathname: Routes.subscribersV4(),
                state: {
                  notInGroups: true,
                },
              }}
            >
              {formatMessage(
                { id: 'group.WithoutSubscribers.link.label' },
                {
                  subscribersCount:
                    localState.subscribersWithoutGroup.subscribersCount,
                },
              )}
            </RouterLink>
          )}
        </Box>
      </AccessControl>

      {shouldShowEmptyView() && (
        <EmptyStateView title={<FormattedMessage id="group.text" />}>
          <AccessControl accesses={[AccessType.ADMINISTRATION]}>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => toggleDialogs(CREATE_GROUP_DIALOG, true)}
            >
              <Icon className={styleClass.extendedIcon}>group_add</Icon>
              <FormattedMessage id="group.action.addGroup.label" />
            </Button>
          </AccessControl>
        </EmptyStateView>
      )}
      {!shouldShowEmptyView() && (
        <div className="flex flex-col flex-auto overflow-hidden">
          <FetchTable
            onClickRow={compose(onViewGroup, prop('id'))}
            FilterComponent={null}
            pages={pageInfo.total}
            data={pageInfo.data}
            error={pageInfo.error}
            refetchData={refetchData}
            className={styleClass.tableContent}
            defaultPageSize={pageSize}
            manual
            loading={pageInfo.isLoading}
            onFetchData={onFetchData}
            noDataText={formatMessage({ id: 'group.table.empty.label' })}
            columns={columns}
            showPagination={showPagination}
            total={pageInfo.totalData}
          />
          <DialogError
            isOpen={openErrorDialog}
            title={formatMessage({
              id: 'group.dialog.memberCard.delete.title',
            })}
            description={formatMessage({
              id:
                checkedData && checkedData.length > 0
                  ? 'group.dialog.memberCard.delete.description.multiple'
                  : 'group.dialog.memberCard.delete.description',
            })}
            closeButton={
              <PrimaryButton
                labelId="action.ok"
                onClick={() => {
                  setOpenErrorDialog(false)
                }}
              />
            }
          />
        </div>
      )}
    </>
  )
}
